import type { TFunction } from "i18next";
import { useState } from "react";
import type { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { emptyPaymentLine, u3125CanManagePayments, type U3125InvoicePaymentGridRow } from "../../../../U3125Service";
import type { U3125FactureNestedParentHandlers } from "../u3125FactureInvoiceLinesTab/U3125InvoiceLineHandlers";

export interface U3125PaymentHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onRowClick: (row: U3125InvoicePaymentGridRow) => void;
  onDlgClose: (result: string) => void;
  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3125InvoicePaymentGridRow>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3125InvoicePaymentGridRow>>;
  dataChangedEvent: number;
  isPersisting: boolean;
}

export interface U3125PaymentHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3125InvoicePaymentGridRow>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3125InvoicePaymentGridRow>>;
  parentFormHandlers: U3125FactureNestedParentHandlers;
  onPersistCreate?: (payment: U3125InvoicePaymentGridRow) => Promise<boolean>;
  onPersistUpdate?: (payment: U3125InvoicePaymentGridRow) => Promise<boolean>;
  onPersistDelete?: (payments: U3125InvoicePaymentGridRow[]) => Promise<boolean>;
}

export function useU3125PaymentHandler(params: U3125PaymentHandlerParam): U3125PaymentHandlerReturn {
  const { gridStateHandlers, formStateHandlers, parentFormHandlers, onPersistCreate, onPersistUpdate, onPersistDelete } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);
  const [isPersisting, setIsPersisting] = useState<boolean>(false);

  const onAdd = (): void => {
    formStateHandlers.setData({ ...emptyPaymentLine(), id: "" } as U3125InvoicePaymentGridRow);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogOpen(true);
  };

  const onModify = (): void => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0] as U3125InvoicePaymentGridRow | undefined;
    if (!selectedRow) return;
    formStateHandlers.setData({ ...selectedRow });
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
  };

  const onDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  const onRowClick = (row: U3125InvoicePaymentGridRow): void => {
    formStateHandlers.setData({ ...row });
    const invoiceStatus = parentFormHandlers.getData?.()?.status;
    const canManage = u3125CanManagePayments(invoiceStatus);
    const parentFormState = parentFormHandlers.getFormState?.();
    if (canManage || parentFormState === U2000FormStateEnumState.EDIT) {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    } else {
      formStateHandlers.setEditMode(U2000FormStateEnumEditMode.NONE);
    }
    setDialogOpen(true);
  };

  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
      return;
    }
    if (result !== "save") {
      setDialogOpen(false);
      return;
    }

    const businessHandlers = formStateHandlers.getBusinessHandlers();
    if (businessHandlers?.validateForm) {
      const errors = businessHandlers.validateForm(formStateHandlers.getData());
      formStateHandlers.setErrors(errors);
      formStateHandlers.saveData();
      if (Object.keys(errors).length > 0) return;
    }

    const saved = formStateHandlers.getData();
    const persist =
      formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT ? onPersistUpdate : onPersistCreate;

    if (!persist) {
      setDialogOpen(false);
      return;
    }

    setIsPersisting(true);
    void persist(saved).then((ok) => {
      setIsPersisting(false);
      if (!ok) return;
      formStateHandlers.afterSaveData(saved);
      setDataChangedEvent((prev) => prev + 1);
      setDialogOpen(false);
    });
  };

  const onDlgDeleteClose = (result: string): void => {
    if (result !== "yes" || !onPersistDelete) {
      setDeleteDialogOpen(false);
      return;
    }
    const selectedRows = gridStateHandlers.getSelectedRows() as U3125InvoicePaymentGridRow[];
    setIsPersisting(true);
    void onPersistDelete(selectedRows).then((ok) => {
      setIsPersisting(false);
      if (!ok) return;
      (gridStateHandlers as any).setSelectedRows?.([]);
      setDataChangedEvent((prev) => prev + 1);
      setDeleteDialogOpen(false);
    });
  };

  return {
    onAdd,
    onModify,
    onDelete,
    onRowClick,
    onDlgClose,
    isDialogOpen,
    setDialogOpen,
    formStateHandlers,
    gridStateHandlers,
    isDeleteDialogOpen,
    onDlgDeleteClose,
    dataChangedEvent,
    isPersisting,
  };
}
